import React, { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { Container, Grid, Box, Button, ButtonGroup, Stack, Typography } from '@mui/material';
// components
import Page from 'src/components/Page';
import { GrayBoxSection } from 'src/sections/GrayBoxSection';
import PlanList from './PlanList';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import SubscriptionPlanManagement from '../dashboard/SubscriptionPlanManagement';
import HubspotContactForm from 'src/components/forms/HubspotContactForm';
import { contactFormConfig } from 'src/components/forms/hubspot-config';
// @types
import { PlanName, PlanType } from 'src/@types/feature';
// styles
import { StyledPlansContainer } from './styles';
import CurrencySelector from 'src/components/locales/CurrencySelector';
import useSubscriptions from 'src/hooks/useSubscriptions';
import { LoadingButton } from '@mui/lab';

export default function PlansPage() {
  const {
    subscriptionPlans,
    onCancelButtonClick,
    onUpdatePaymentMethod,
    isPremiumUser,
    isCurrentPlanEnterprise,
    isPlansLoading,
    subscriptionsAreLoading,
    isStripeSubscription,
    planCurrency,
    setPlanCurrency,
  } = useSubscriptions();

  const [selectedBtn, setSelectedBtn] = useState(PlanType.MONTHLY);
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);
  const [updateBillingMethodIsLoading, setUpdateBillingMethodIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const currenciesForSelector = useMemo(() => {
    const list = subscriptionPlans?.find(plan => plan?.planLevel === PlanName.STARTER)?.prices;
    return [...new Set(list?.map(item => item?.currency?.toUpperCase()))]
  }, [subscriptionPlans]);

  return (
    <Page title="Subscriptions">
      <Container maxWidth={false}>
        <HeaderBreadcrumbs links={[{ name: 'Account' }, { name: 'Subscription plans' }]} />

        <StyledPlansContainer container rowSpacing={1} flexWrap='wrap-reverse'>
          <Grid item xs={13} md={4} />

          <Grid item xs={12} sm={6} md={4} className='buttonGroup'>
            <ButtonGroup disableRipple disableElevation>
              <Button
                onClick={() => setSelectedBtn(PlanType.MONTHLY)}
                color={selectedBtn === PlanType.MONTHLY ? 'primary' : 'secondary'}
                variant='contained'
                sx={{ mr: 0.5 }}
              >
                MONTHLY
              </Button>
              <Button
                onClick={() => setSelectedBtn(PlanType.ANNUAL)}
                color={selectedBtn === PlanType.ANNUAL ? 'primary' : 'secondary'}
                variant='contained'
              >
                ANNUAL
              </Button>
            </ButtonGroup>
          </Grid>

          {(!isPremiumUser && !subscriptionsAreLoading && !isPlansLoading) ?
            <Grid item xs={12} sm={6} md={4} className='currencySelector'>
              <Stack spacing={1} direction='row'>
                <Typography variant='caption'>CHOOSE YOUR CURRENCY</Typography>
                <CurrencySelector
                  currencies={currenciesForSelector}
                  currency={planCurrency}
                  selectedCurrency={setPlanCurrency}
                />
              </Stack>
            </Grid> : null
          }
        </StyledPlansContainer>

        <Stack spacing={3}>
          <PlanList
            selectedBtn={selectedBtn}
            subscriptionPlans={subscriptionPlans}
          />

          <SubscriptionPlanManagement />

          {isPremiumUser && !isCurrentPlanEnterprise && 
            <GrayBoxSection title={'Manage payment and billing'}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1.5 }}>Manage your payment details</Typography>

                <Box sx={{ display: 'flex', gap: { xs: 0, sm: 2 }, flexWrap: 'wrap' }}>
                  <LoadingButton
                    variant="outlined"
                    loading={updateBillingMethodIsLoading}
                    onClick={() => {
                        if (!subscriptionsAreLoading && isStripeSubscription) {
                            setIsOpenContactModal(true)
                        } else {
                            setUpdateBillingMethodIsLoading(true)
                            onUpdatePaymentMethod()
                                .catch(() => {
                                    setUpdateBillingMethodIsLoading(false);
                                    enqueueSnackbar('There was a network error. Please, try again later or contact us directly', { variant: 'error' });
                                });
                        }
                    }}
                    size='small'
                  >
                    Manage billing information
                  </LoadingButton>
                  <Button
                    onClick={() => {
                      !subscriptionsAreLoading && isStripeSubscription
                        ? setIsOpenContactModal(true)
                        : onCancelButtonClick()
                    }}
                    size='small'
                  >
                    Cancel subscription
                  </Button>
                </Box>
              </Box>
            </GrayBoxSection>
          }

        </Stack>
      </Container>

      {isOpenContactModal && (
        <HubspotContactForm
          isModalOpen={isOpenContactModal}
          setIsModalOpen={setIsOpenContactModal}
          region={contactFormConfig.region}
          portalId={contactFormConfig.portalId}
          formId={contactFormConfig.formId}
        />
      )}
    </Page>
  );
}
